import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FiCheck, FiCode, FiGlobe, FiClock, FiSettings, FiExternalLink } from 'react-icons/fi';
import { WIDGETS } from './catalog';
import { useWidgetSite } from './shared';

type Filter = 'all' | 'installed' | 'missing';

// Widgets › step 2 (spec 408:11942)
const WidgetList: React.FC = () => {
    const history = useHistory();
    const { host } = useWidgetSite();
    const [filter, setFilter] = useState<Filter>('all');
    const installed = WIDGETS.filter(w => w.installed);
    const missing = WIDGETS.length - installed.length;
    const pages = new Set(installed.map(w => w.page)).size;
    const shown = WIDGETS.filter(w => filter === 'all' || (filter === 'installed') === w.installed);

    const open = (slug: string) => history.push(`/client-settings/widgets/${slug}`);
    const chips: { value: Filter; label: string; count: number }[] = [
        { value: 'all', label: 'All', count: WIDGETS.length },
        { value: 'installed', label: 'Installed', count: installed.length },
        { value: 'missing', label: 'Missing', count: missing },
    ];

    return <>
        <div className="wd-stats">
            <div className="card stat">
                <div className="h"><span className="icon-tile"><FiCheck /></span><span>Widgets installed</span>{missing > 0 && <span className="badge warn">{missing} missing</span>}</div>
                <strong>{installed.length} of {WIDGETS.length}</strong>
                <p>Last checked 5 minutes ago</p>
            </div>
            <div className="card stat">
                <div className="h"><span className="icon-tile"><FiCode /></span><span>Investi script</span></div>
                <strong>Installed</strong>
                <p>Found in the website header · v2.4</p>
            </div>
            <div className="card stat">
                <div className="h"><span className="icon-tile"><FiGlobe /></span><span>Pages using widgets</span></div>
                <strong>{pages}</strong>
                <p>Across {host}</p>
            </div>
            <div className="card stat">
                <div className="h"><span className="icon-tile"><FiClock /></span><span>Widget version</span></div>
                <strong>Up to date</strong>
                <p>Widgets update themselves</p>
            </div>
        </div>

        <section className="card">
            <div className="section-title">
                <span className="icon-tile"><FiSettings /></span>
                <div className="text">
                    <h2>Step 2 · Widgets</h2>
                    <div className="hint">Each widget is a short code you paste into a WordPress page. Investi checks the website every hour.</div>
                </div>
                <div className="wd-chips" role="tablist" aria-label="Show">
                    {chips.map(c => <button key={c.value} type="button" role="tab" aria-selected={filter === c.value}
                        className={filter === c.value ? 'active' : ''} onClick={() => setFilter(c.value)}>
                        {c.label} <span>{c.count}</span>
                    </button>)}
                </div>
            </div>

            <div className="wd-table">
                <div className="tr th">
                    <span className="w">Widget</span>
                    <span className="where">Where it is on the website</span>
                    <span className="status">Status</span>
                    <span className="act" />
                </div>
                {shown.map(w => {
                    const where = w.page ?? w.where ?? w.expectedPage ?? 'Not found on the website';
                    const red = !w.installed && w.mustInstall;
                    return <div key={w.slug} className="tr" onClick={() => open(w.slug)}>
                        <span className="w">
                            <span className="icon-tile">{w.icon}</span>
                            <span className="t"><strong>{w.name}</strong><span>{w.description}</span></span>
                        </span>
                        <span className={`where${red ? ' red' : ''}`}>{where}</span>
                        <span className="status"><span className={`badge ${w.installed ? 'ok' : 'bad'}`}>{w.installed ? 'Installed' : 'Not installed'}</span></span>
                        <span className="act" onClick={e => e.stopPropagation()}>
                            {red
                                ? <button type="button" className="btn-primary with-icon" onClick={() => open(w.slug)}><FiCode /> Install Now</button>
                                : <button type="button" className="btn-outline with-icon" onClick={() => open(w.slug)}><FiCode /> Get Code</button>}
                            <a className="icon-btn" href={`https://${host}${w.page ?? '/'}`} target="_blank" rel="noopener noreferrer" aria-label={`Open ${w.name} on the website`}><FiExternalLink /></a>
                        </span>
                    </div>;
                })}
            </div>
        </section>
    </>;
};

export default WidgetList;
